/**
 * IMBEE-8069 (T86) — the welcome banner (Area A) and bottom quick-button bar (Area C)
 * portions of the widget settings returned by GET /widget.
 *
 * These mirror the service contract exactly (web-chat-widget.interface.ts). Every field is
 * optional: a channel configured before this feature shipped, or one with the feature off,
 * simply omits them, and the widget must render exactly as it does today.
 */

export enum WelcomeBannerImageMode {
    Default = 'default',
    Client = 'client',
}

export type WelcomeBannerBadge = {
    /** 6-digit hex, no leading '#'. */
    color: string;
    text: string;
};

export type WelcomeBannerButton = {
    label: string;
    badge?: WelcomeBannerBadge;
};

export type BottomButton = {
    label: string;
};

/**
 * The Area A + Area C slice of the widget settings, travelling as one prop from App down to
 * Conversation rather than eight separate ones through three components.
 */
export type QuickReplyConfig = {
    enableWelcomeBanner?: boolean;
    welcomeBannerImageMode?: WelcomeBannerImageMode;
    welcomeBannerImageUrl?: string | null;
    welcomeBannerButtons?: WelcomeBannerButton[];
    enableBottomButtons?: boolean;
    bottomButtons?: BottomButton[];
    /** IMBEE-8955 (T115) — travels with the same prop rather than a second one. */
    operatingHours?: OperatingHoursConfig;
};

/** AC-A.2 — the banner shows this many buttons at a time, then pages. */
export const WELCOME_BANNER_BUTTONS_PER_PAGE = 4;

/**
 * AC-A.2 — the banner's buttons split into pages. Order is preserved; the last page may be
 * short. An empty list yields no pages at all, so the banner can hide its pager entirely
 * rather than render "page 1 of 0".
 */
export const paginateBannerButtons = (
    buttons: WelcomeBannerButton[] = [],
    perPage: number = WELCOME_BANNER_BUTTONS_PER_PAGE,
): WelcomeBannerButton[][] => {
    const size = Math.max(1, perPage);
    const pages: WelcomeBannerButton[][] = [];
    for (let i = 0; i < buttons.length; i += size) {
        pages.push(buttons.slice(i, i + size));
    }
    return pages;
};

/**
 * AC-A.3 — a badge renders only when it carries BOTH a colour and a text. Half a badge is an
 * empty coloured pill, which reads as a rendering bug rather than as "no badge".
 */
export const hasRenderableBadge = (button: WelcomeBannerButton): boolean =>
    Boolean(button.badge && button.badge.color && button.badge.text);


/**
 * IMBEE-9231 (T86 AC-C.1) — read the visitor's bottom buttons off whatever the network gave us.
 *
 * The widget no longer decides which buttons a visitor may see. It used to hold the ticket's
 * tags and run the displayCondition predicate itself, which meant internal CRM state — VIP,
 * chargeback risk — had to be shipped to the browser. AC-1 decided none of it is publishable,
 * so web-chat-services evaluates the conditions and sends the surviving labels; this just
 * renders them.
 *
 * Two untrusted sources feed it: `bottom_buttons` on the session create/restore response, and
 * the body of a `{sessionId}-bottom-buttons` stomp frame, which is a JSON.parse of a string.
 *
 * Returns null for anything unusable, and callers IGNORE a null rather than applying it. That
 * matters: coercing to [] would blank a working bar because one malformed frame arrived,
 * whereas keeping the previous list is both safer and self-correcting — the next push or the
 * next session restore replaces it.
 */
export const normalizeBottomButtons = (value: unknown): BottomButton[] | null =>
    Array.isArray(value)
        ? value
              .filter(
                  (button): button is BottomButton =>
                      Boolean(button) &&
                      typeof (button as BottomButton).label === 'string' &&
                      (button as BottomButton).label.length > 0,
              )
              .map(button => ({ label: button.label }))
        : null;
/* ---- IMBEE-8955 (T115) Operating hours ---------------------------------------------------
 *
 * The widget decides open/closed for itself from the schedule it is given. AC-7.3 envisages the
 * service also sending a precomputed state and a nextBoundary; until that exists, computing here
 * is what makes the feature work, and it has to agree with the other two implementations of the
 * same rule or the operator configures one thing and the visitor sees another:
 *
 *   core-php   WebChat::isOpenAt          — labels the ticket afterwards
 *   imbee-cms  Channel/operatingHours.ts  — the admin preview, what the operator is promised
 *
 * On every input a configured channel can actually have, all three are line-for-line equivalent,
 * inclusive-open/exclusive-close included. ONE input diverges, and it is disclosed rather than
 * papered over: a zone the running browser cannot use. This file and core-php both fail OPEN
 * there (see isOpenNow); the imbee-cms preview still evaluates against the admin's own clock.
 * That is deliberate for now, not an oversight — the preview's zone comes from the SAME browser
 * that then formats it (availableTimezones() reads Intl.supportedValuesOf), so an unusable zone
 * is unreachable in the preview and reachable in the widget. Aligning it anyway is IMBEE-9657,
 * because "unreachable" is a property of today's selector, not of the function.
 */

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = typeof WEEKDAYS[number];

export type OperatingHoursBlock = { open: string; close: string };
export type OperatingHoursSchedule = Partial<Record<Weekday, OperatingHoursBlock[]>>;

export type OperatingHoursConfig = {
    enableOperatingHours?: boolean;
    operatingHoursTimezone?: string;
    operatingHoursSchedule?: OperatingHoursSchedule;
    operatingHoursHolidays?: string[];
    awayMessage?: string;
    responseTimeInHours?: string;
    responseTimeReopen?: string;
};

/** "HH:MM" as minutes past midnight; null for anything not in that form. */
export const minutesOfDay = (time: string): number | null => {
    if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(time ?? '')) return null;
    const [hours, minutes] = time.split(':');
    return Number(hours) * 60 + Number(minutes);
};

/**
 * Can this browser evaluate a schedule in this zone at all?
 *
 * Reachable, and asymmetrically so. core-php validates the zone against
 * DateTimeZone::listIdentifiers() at save time (ApiKeyWebChatCommonForm.php:432-446), so the
 * stored value is a real IANA name — but PHP's zone database and the visitor's browser ICU are
 * two different databases on two different release cadences. A zone added recently (Europe/Kyiv,
 * America/Ciudad_Juarez) is known to the server and to the admin's browser while an older mobile
 * WebView still throws RangeError on it. The admin picks from a list their OWN browser produced;
 * the visitor is handed a string somebody else's browser chose. That asymmetry is the whole of
 * this edge case.
 */
export const isUsableTimezone = (timezone: string): boolean => {
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: timezone || 'UTC' });
        return true;
    } catch {
        return false;
    }
};

/**
 * The weekday and wall-clock minute it is in a given IANA zone.
 *
 * Via Intl rather than a fixed offset, which is what makes it DST-correct: Europe/London is +0
 * in January and +1 in July, and an offset table does not know that.
 *
 * An empty zone means UTC, matching the server's `new DateTimeZone($timezone ?: 'UTC')`
 * (WebChat.php:833) — the widget and the server must answer in the same zone for the same
 * channel, or a conversation is labelled on one side of closing time and shown on the other.
 * See isUsableTimezone above and isOpenNow below. An unusable zone never reaches here: isOpenNow
 * and nextBoundary both stop before calling it, so this function is only ever asked about a zone
 * Intl accepts.
 */
export const nowInTimezone = (
    timezone: string,
    now: Date = new Date(),
): { weekday: Weekday; minutes: number } => {
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    };
    let parts: Intl.DateTimeFormatPart[];
    try {
        parts = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: timezone || 'UTC' }).formatToParts(now);
    } catch {
        parts = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' }).formatToParts(now);
    }
    const find = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
    const weekday = find('weekday').slice(0, 3).toLowerCase() as Weekday;
    return { weekday, minutes: (Number(find('hour')) % 24) * 60 + Number(find('minute')) };
};

/** The calendar date in that zone, for matching the holiday list. UTC fallback, as above. */
export const dateInTimezone = (timezone: string, now: Date = new Date()): string => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
    try {
        return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: timezone || 'UTC' }).format(now);
    } catch {
        return new Intl.DateTimeFormat('en-CA', { ...options, timeZone: 'UTC' }).format(now);
    }
};

/**
 * AC-7.2 — is the channel open right now?
 *
 * A holiday closes the whole day whatever the weekday schedule says (AC-3.2). Open is inclusive
 * and close exclusive, so two touching blocks read as one continuous span.
 *
 * A zone this browser cannot use reports OPEN and evaluates nothing. That is the server's rule,
 * quoted from WebChat.php:837-841 — "An unusable zone must not decide that a
 * channel is shut" — and matching it is the point: the widget decides what the visitor sees,
 * isOpenAt decides what param_opened_outside_operating_hours records afterwards, and this file's
 * opening comment requires the two to agree. They did not. The widget evaluated the schedule
 * against the VISITOR'S clock while the server said open, so a visitor could be shown the away
 * panel, submit through the offline form, and have the ticket land unlabelled — the enquiry the
 * §3.3 capture metric exists to count, absent from the Inbox filter that counts it.
 *
 * Fail-open is also the safe direction on its own terms: the cost is a reply that arrives slower
 * than promised, against a visitor being turned away from a channel that is open.
 */
export const isOpenNow = (config: OperatingHoursConfig = {}, now: Date = new Date()): boolean => {
    const timezone = config.operatingHoursTimezone ?? '';
    if (!isUsableTimezone(timezone)) return true;
    if ((config.operatingHoursHolidays ?? []).includes(dateInTimezone(timezone, now))) return false;
    const { weekday, minutes } = nowInTimezone(timezone, now);
    return (config.operatingHoursSchedule?.[weekday] ?? []).some((block) => {
        const open = minutesOfDay(block.open);
        const close = minutesOfDay(block.close);
        if (open === null || close === null) return false;
        /*
         * No midnight wraparound, and that is the PRODUCER's rule rather than an omission here.
         * core-php rejects `close <= open` at save time (ApiKeyWebChatCommonForm.php:452-456):
         * "An overnight span is expressed as two blocks on two days; allowing close <= open
         * would make 'open all day' and 'closed' indistinguishable." So 22:00-02:00 never
         * reaches this browser as one block — it arrives as 22:00-23:59 on one day and
         * 00:00-02:00 on the next, both of which this comparison handles.
         *
         * A wraparound reading here would therefore not fix anything reachable; it would give a
         * second, disagreeing meaning to input the source of truth has already ruled out. Such a
         * block reads as closed, which is deterministic and matches what the producer says the
         * shape means: nothing.
         */
        return minutes >= open && minutes < close;
    });
};

/**
 * AC-1.3 / AC-7.2 — should the widget show its closed state?
 *
 * Only when the feature is switched on. With it off the widget behaves exactly as it always
 * has, whatever else happens to be configured.
 */
export const isAwayNow = (config: OperatingHoursConfig = {}, now: Date = new Date()): boolean =>
    Boolean(config.enableOperatingHours) && !isOpenNow(config, now);

/**
 * IMBEE-8955 (T115) — why the widget is closed, for `webchat_offline_view_shown`.
 *
 * A holiday closure and an ordinary out-of-hours visit look identical to a visitor but mean
 * different things to whoever reads the metric: one is a planned shutdown, the other is just
 * evening. Null when the channel is open, so the caller cannot report a reason for a state the
 * visitor is not in.
 */
export const awayReason = (
    config: OperatingHoursConfig = {},
    now: Date = new Date(),
): 'schedule' | 'holiday' | null => {
    if (!isAwayNow(config, now)) return null;
    const timezone = config.operatingHoursTimezone ?? '';
    return (config.operatingHoursHolidays ?? []).includes(dateInTimezone(timezone, now))
        ? 'holiday'
        : 'schedule';
};

/**
 * AC-7.3 — when the state next changes, so the widget can re-check without polling hard.
 *
 * Returns the next block edge strictly after `now`, scanning forward up to a week, skipping days
 * the channel has marked as holidays. Null when nothing can change — nothing configured, or a
 * holiday-free always-closed week.
 *
 * A null means the caller schedules NOTHING. There is no periodic re-check anywhere in this
 * widget to fall back on — the caller at Conversation/index.tsx simply returns — so a null is
 * the widget's final answer for this config, not a deferral.
 */
export const nextBoundary = (config: OperatingHoursConfig = {}, now: Date = new Date()): Date | null => {
    if (!config.enableOperatingHours) return null;
    const timezone = config.operatingHoursTimezone ?? '';
    // An unusable zone is permanently open (isOpenNow above), so there is no transition to wake
    // for. Returning null here rather than scanning keeps the two functions from disagreeing.
    if (!isUsableTimezone(timezone)) return null;
    const { weekday, minutes } = nowInTimezone(timezone, now);
    const startIndex = WEEKDAYS.indexOf(weekday);
    if (startIndex < 0) return null;
    const holidays = config.operatingHoursHolidays ?? [];

    /**
     * The absolute instant at which the channel's LOCAL clock next reads `edgeMinutes`.
     *
     * NOT `now.getTime() + wallMinutesAway * 60_000`, however much it looks like it should be.
     * That spends wall-clock minutes as if they were elapsed real time, and across a DST
     * transition they are not: a 24-hour wall-clock gap is 23 real hours on spring-forward and
     * 25 on fall-back, so the boundary lands an hour out and the widget shows "closed" for an
     * hour on a channel that is open.
     *
     * Measured rather than assumed: take the naive guess, ask what the local clock actually
     * reads there, shift by the difference. Two passes converge — the first guess is wrong by at
     * most the DST offset.
     *
     * A wall-clock time that does not exist — the spring-forward gap, e.g. 02:30 where the clock
     * jumps 02:00 to 03:00 — cannot be converged onto, so the loop stops after two passes and
     * returns the instant the same number of REAL minutes past the jump. Measured on
     * America/New_York, 2026-03-08:
     *
     *     edge 02:00 -> 03:00 local        (the transition itself, because the gap starts there)
     *     edge 02:15 -> 03:15 local
     *     edge 02:30 -> 03:30 local
     *     edge 02:59 -> 03:59 local
     *
     * So only the gap's first minute coincides with the transition; the rest land that far into
     * the hour after it. That is a deliberate choice rather than the only possible one — elapsed
     * real time is preserved, and a schedule edge inside a skipped hour has no exact answer. The
     * alternative, clamping every gap minute to the transition, would make a 02:59 edge fire 59
     * minutes early. Pinned by the gap tests in widgetSettings.test.ts.
     */
    const instantAtLocalMinutes = (wallMinutesAway: number, edgeMinutes: number): Date => {
        let guess = new Date(now.getTime() + wallMinutesAway * 60_000);
        for (let pass = 0; pass < 2; pass += 1) {
            let drift = edgeMinutes - nowInTimezone(timezone, guess).minutes;
            if (drift === 0) break;
            // Normalise across midnight: a -1430 drift is really +10 minutes.
            if (drift > 720) drift -= 1440;
            if (drift < -720) drift += 1440;
            guess = new Date(guess.getTime() + drift * 60_000);
        }
        return guess;
    };

    for (let offset = 0; offset < 8; offset += 1) {
        const day = WEEKDAYS[(startIndex + offset) % 7];
        const edges = (config.operatingHoursSchedule?.[day] ?? [])
            .flatMap((block) => [minutesOfDay(block.open), minutesOfDay(block.close)])
            .filter((edge): edge is number => edge !== null)
            .sort((a, b) => a - b);
        for (let i = 0; i < edges.length; i += 1) {
            const wallMinutesAway = offset * 1440 + edges[i] - minutes;
            if (wallMinutesAway <= 0) continue;
            const instant = instantAtLocalMinutes(wallMinutesAway, edges[i]);
            if (instant.getTime() <= now.getTime()) continue;
            /*
             * Holidays are checked on the instant's OWN local date rather than on a date derived
             * by adding offset days to today. The channel is closed all day on a
             * holiday, so neither of that day's edges is a state change. Deriving the date from
             * the instant we are about to return keeps the check exact across a DST shift, where
             * adding 24h of milliseconds can land on the wrong calendar date near midnight.
             */
            if (holidays.includes(dateInTimezone(timezone, instant))) continue;
            return instant;
        }
    }
    return null;
};

/**
 * IMBEE-8955 (T115) AC-8.1 / AC-8.2 — which open-state event this visit is, with what properties.
 *
 * Extracted from App.tsx's once-per-open effect so the NAME→PROPERTY mapping carries a test. The
 * effect itself is not reachable from a unit test — it needs the whole Redux + widget-config
 * stack stood up — and this mapping is exactly the part that went wrong once already: the
 * in-hours property was called `greetingShown` while measuring whether a response time was
 * CONFIGURED (fixed in 64a27126). A wrong name here throws nothing and renders nothing; it
 * silently reports a different number to whoever reads the metric, which is why it needs a
 * mechanical check rather than a careful reader.
 *
 * Null when the feature is off — the widget then behaves as it always has and reports nothing.
 * The caller must keep its own once-per-open latch: this function is pure and will happily
 * answer the same question twice.
 */
export const openStateEvent = (
    config: OperatingHoursConfig = {},
    prechatShown = false,
    now: Date = new Date(),
): { name: string; properties: Record<string, unknown> } | null => {
    if (!config.enableOperatingHours) return null;
    const reason = awayReason(config, now);
    return reason
        ? { name: 'webchat_offline_view_shown', properties: { reason, prechatShown } }
        : {
              name: 'webchat_in_hours_open',
              // Trimmed, to match the DISPLAY gates. StatusNotice and Conversation both test
              // `!responseTimeInHours?.trim()` before rendering anything, so a whitespace-only
              // value shows the visitor nothing — and an untrimmed Boolean here would report a
              // response time as configured while the widget displayed none. The metric has to
              // agree with the screen; disagreeing quietly is the same defect as the rename.
              properties: { responseTimeConfigured: Boolean(config.responseTimeInHours?.trim()) },
          };
};

/**
 * IMBEE-8955 (T115) AC-8.2 — a one-shot latch. True the first time, false ever after.
 *
 * The Pre-chat submit handler emits the NUMERATOR of the >=60% capture metric from inside an
 * async Formik `onSubmit` that awaits 500ms before doing anything. Formik v2 puts no re-entrancy
 * guard on `handleSubmit`, and `disabled={isSubmitting}` only blocks a second CLICK once React
 * has re-rendered — an Enter-key resubmit racing that await re-enters and would emit twice for
 * one visitor, double-counting the number the feature is judged by.
 *
 * A function rather than an inline `if (!ref.current) ref.current = true`, so the contract can be
 * asserted: this pins that a second call returns false. It does NOT pin the Formik race, which
 * needs the real form; see the test file for why a test that renders it but cannot re-enter would
 * be worse than none.
 */
export const latchOnce = (latch: { current: boolean }): boolean => {
    if (latch.current) return false;
    latch.current = true;
    return true;
};
